/**
 * The error a client call throws.
 *
 * One class with a `kind` rather than four subclasses, because callers almost always want to
 * distinguish *what to do* — retry, show a field message, re-authenticate, show a degraded banner —
 * and that is a short list. Four subclasses would also make `instanceof` the discriminator, which
 * breaks the moment two copies of this package end up in one bundle.
 *
 * `problem` carries the server's RFC 9457 body when there was one, unmodified, so a caller can reach
 * the extension fields this class does not know about: `unsatisfiable` on a 422 and `degraded` on a
 * 503 are the two that already exist, and a client that had to pattern-match on status to find them
 * would have to be updated whenever one is added.
 */

import type { Problem } from "@trippool/contracts";

export type ApiErrorKind =
  /** The request never got an answer: DNS, TLS, timeout, or the caller aborted it. */
  | "network"
  /** An HTTP error status. `problem` is present when the body matched the contract. */
  | "http"
  /** The response did not match the contract. The server and this build disagree. */
  | "invalid-response";

export interface ApiErrorInit {
  kind: ApiErrorKind;
  /** Vietnamese, ready to show. Falls back to the server's title when there is a problem body. */
  message: string;
  url: string;
  operationId: string;
  status?: number;
  detail?: string;
  problem?: Problem;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  /** The full request URL. Carried because it is what makes a log line actionable. */
  readonly url: string;
  /** The registry's operation id, so a caller can tell which call failed without parsing the URL. */
  readonly operationId: string;
  readonly status: number | undefined;
  readonly detail: string | undefined;
  /** The parsed problem body, when the server sent one. */
  readonly problem: Problem | undefined;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause !== undefined ? { cause: init.cause } : undefined);
    this.name = "ApiError";
    this.kind = init.kind;
    this.url = init.url;
    this.operationId = init.operationId;
    this.status = init.status;
    this.detail = init.detail;
    this.problem = init.problem;
  }

  /**
   * The problem `type` slug, when there is one.
   *
   * Convenience for the common branch. A caller matching on the slug rather than the status is
   * matching on what went wrong rather than how it was encoded — the same slug could be served with a
   * different status in a later version, and `/api/v1` promises the slug is the stable part.
   */
  get problemType(): string | null {
    if (this.problem === undefined) return null;
    return this.problem.type.split("/").pop() ?? null;
  }

  /** Validation failures, keyed by field path, for a form to render. */
  get fieldErrors(): Array<{ field: string; message: string }> {
    if (this.problem === undefined || this.problem.status !== 400) return [];
    return this.problem.errors;
  }

  /** Hours-or-seconds hint from a 429. */
  get retryAfterSeconds(): number | null {
    if (this.problem === undefined || this.problem.status !== 429) return null;
    return this.problem.retry_after;
  }

  /** Which capabilities are down, from a 503. */
  get degraded(): string[] {
    if (this.problem === undefined || this.problem.status !== 503) return [];
    return this.problem.degraded;
  }

  /**
   * Which constraints could not all hold, from a 422.
   *
   * Not an error to hide: the contract requires the client to show which constraint gives (AC-4.5).
   */
  get unsatisfiable(): string[] {
    if (this.problem === undefined || this.problem.status !== 422) return [];
    return this.problem.unsatisfiable;
  }

  /** Whether retrying the same call could succeed. */
  get isRetryable(): boolean {
    if (this.kind === "network") return true;
    if (this.status === undefined) return false;
    return this.status === 429 || this.status === 502 || this.status === 503 || this.status === 504;
  }

  /** Whether the session is the problem, so the caller should re-authenticate. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}
